import React, { useCallback, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import {
    actionAdyenPaymentResponse,
    actionAdyenRedirection,
    fetchAdyenPaymentMethods,
    resetCart,
    sendAdyenPayment
} from 'shared/store/ecommerce/order'
import {
    ecommerceType,
    errors,
    neptingStatus,
    PaymentProvider
} from 'shared/utils'
import { ErrorBanner } from '../../components'
import { CartUserInfos } from './CartUserInfosContainer'
import { creditCardType, useStoredCard } from 'shared/utils/ecommerce'

const { PAYMENT_SUCCESS } = neptingStatus
const { DELIVERY_IN_APP } = ecommerceType

export const PaymentWrapper = () => {
    const orderState = useSelector(state => state?.ecommerceOrderState)
    const { authenticated } = useSelector(state => state?.userState)
    const { creditCards } = useSelector(state => state.kingdomProfilePageState)
    const { restaurant, ecommerceMode, adyen } = orderState
    const { paymentResponse, paymentMethods } = orderState?.adyen
    const history = useHistory()
    const location = useLocation()

    const dispatch = useDispatch()
    const [error, setError] = useState(null)
    const intl = useIntl()
    const [isLoading, setIsLoading] = useState(false)
    const isAdyenRestaurant =
        restaurant?.paymentServiceProvider === PaymentProvider.ADYEN
    const isDelivery = ecommerceMode === DELIVERY_IN_APP

    const clearError = useCallback(() => {
        dispatch(actionAdyenRedirection(undefined))
        dispatch(actionAdyenPaymentResponse(null))
        setError(null)
    }, [dispatch])

    useEffect(() => {
        // Reset the store for new order
        clearError()
    }, [clearError])

    useEffect(() => {
        if (restaurant?.paymentServiceProvider !== PaymentProvider.ADYEN) return

        if (!paymentMethods) {
            dispatch(fetchAdyenPaymentMethods(restaurant?.id))
        }
    }, [restaurant, dispatch, paymentMethods])

    useEffect(() => {
        if (orderState?.adyen.redirection?.orderId) {
            const path = intl.formatMessage({
                id: 'routes.clickandcollect.cart.confirmation'
            })
            history.push(
                `${path}?id=${orderState?.adyen.redirection?.orderId}&token=${orderState?.adyen.redirection?.orderToken}`
            )
        }
    }, [orderState?.adyen.redirection])

    useEffect(() => {
        if (restaurant?.paymentServiceProvider !== PaymentProvider.ADYEN) return

        const resultCode = paymentResponse?.resultCode

        if (paymentResponse?.refusalReasonCode || resultCode) {
            setIsLoading(false)
        }

        if (!resultCode) {
            return
        }

        switch (resultCode.toUpperCase()) {
            case 'REFUSED': {
                setError({
                    message: 'clickandcollect.payment.error.message',
                    customReason: paymentResponse?.refusalReason
                })
                break
            }
            case 'CANCELLED': {
                setError({
                    message: 'clickandcollect.payment.error.message',
                    reason: 'clickandcollect.payment.error.reason.cancelled'
                })
                break
            }
            default:
                break
        }
    }, [
        dispatch,
        paymentResponse?.refusalReasonCode,
        paymentResponse?.resultCode,
        restaurant?.paymentServiceProvider,
        setError,
        history,
        setIsLoading
    ])

    const navigateToCartWithError = (reason?: string) => {
        const to = intl.formatMessage({
            id: 'routes.clickandcollect.cart.summary'
        })
        const state = {
            error: {
                message: 'clickandcollect.error.validation',
                reason
            }
        }
        history.push(to, state)
    }

    const manageError = (error: string) => {
        switch (error) {
            case errors.PROMOTION_NOT_VALID:
                navigateToCartWithError('clickandcollect.error.promo')
                break
            case errors.MISSING_PRODUCTS:
                navigateToCartWithError('clickandcollect.error.missing')
                break
            case errors.NETWORK:
                setError({ message: 'error.network.alert.message' })
                break
            case errors.PRICE_DIFFERENCE:
                navigateToCartWithError('clickandcollect.error.price')
                break
            case errors.REJECTED:
                navigateToCartWithError()
                break
            case errors.FEATURE_NOT_AVAILABLE:
                setError({ message: 'clickandcollect.error.closed' })
                break
            case errors.ABUSIVE_ORDER:
                navigateToCartWithError('clickandcollect.error.abuse')
                break
            case errors.PAYMENT_CANCELLED:
                setError({
                    message: 'clickandcollect.payment.error.message',
                    reason: 'clickandcollect.payment.error.reason.cancelled'
                })
                break
            case errors.LEAP_YEAR_OPERATION_TOTAL_LIMIT_REACHED:
                setError({
                    message: 'form.coupon.error.leap.year.total.limit.reached'
                })
                break
            case errors.PAYMENT_REFUSED:
                setError({
                    message: 'clickandcollect.payment.error.message',
                    reason: 'clickandcollect.payment.error.reason.refused'
                })
                break
            case errors.PAYMENT_ERROR:
                setError({
                    message: 'clickandcollect.payment.error.message',
                    reason: 'clickandcollect.payment.error.reason.refused'
                })
                break
            case errors.TABLE_ORDER_BAD_TOKEN:
                setError({
                    message: 'clickandcollect.payment.error.message',
                    reason:
                        'clickandcollect.payment.error.reason.table-order.bad-token'
                })
                break
            default:
                setError({ message: 'form.error.default' })
                break
        }
    }

    useEffect(() => {
        const neptingStatus = location.search.split('status=')[1]
        const { currentToken, cacOrders, deliveryOrders } = orderState
        const orders = { ...cacOrders, ...deliveryOrders }
        if (
            (neptingStatus === PAYMENT_SUCCESS && orders[currentToken]) ||
            (authenticated &&
                orders[currentToken] &&
                orders[currentToken].orderStatus === 'PAID')
        ) {
            const path = intl.formatMessage({
                id: 'routes.clickandcollect.cart.confirmation'
            })
            dispatch(resetCart())
            history.push(
                `${path}?id=${orders[currentToken].id}&token=${currentToken}`
            )
        }
    }, [location, orderState])

    const handleAdyenPayment = async (data: any) => {
        try {
            await dispatch(
                sendAdyenPayment({
                    paymentMethod: data.paymentMethod,
                    browserInfo: data.browserInfo,
                    storePaymentMethod: data.storePaymentMethod,
                    channel: 'Web'
                })
            )
        } catch (error) {
            manageError(error)
        }
    }

    const errorMessage =
        error &&
        error.message &&
        intl.formatMessage({
            id: error.message
        })
    const errorReason =
        (error && error.customReason) ||
        (error &&
            error.reason &&
            intl.formatMessage({
                id: error.reason
            }))

    // const { storedCards, cardIdSelected } = useStoredCard({
    //     profileCreditCards: creditCards,
    //     creditCardsToFilter: isAdyenRestaurant
    //         ? adyen?.paymentMethods?.storedPaymentMethods
    //         : creditCards,
    //     isAdyenRestaurant,
    //     authenticated,
    //     isDelivery
    // })

    const storedCards = isAdyenRestaurant
        ? adyen?.paymentMethods?.storedPaymentMethods
        : creditCards

    const cardIdSelected = storedCards?.[0]?.id || creditCardType.CB

    return (
        <>
            {!!errorMessage && (
                <ErrorBanner
                    isCart
                    text={errorMessage}
                    description={errorReason}
                    onClose={clearError}
                />
            )}

            <CartUserInfos
                handleAdyenPayment={handleAdyenPayment}
                manageError={manageError}
                setError={setError}
                clearError={clearError}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                storedCards={storedCards}
                cardIdSelected={cardIdSelected}
            />
        </>
    )
}
